import { QueryKey } from "react-query";
import { AnteTest, CacheKeys } from "types";
import queryClient from "utils/queryClient";

const keysPrefix = "ante-tests";

const keys: CacheKeys = {
  all: () => [keysPrefix],

  filtered: (filters?: Record<string, any>) =>
    [...keys.all(), "filtered", filters].filter((value) => value !== undefined),

  single: (chainId: string, address: string) => [
    ...keys.all(),
    "single",
    chainId,
    address,
  ],
};

const updateOrInvalidateCache = (key: QueryKey, updater: any) => {
  const newData = queryClient.setQueryData(key, updater);
  if (newData === undefined) {
    queryClient.invalidateQueries({
      queryKey: key,
      exact: true,
      refetchActive: false,
    });
  }
};

const updateOrInvalidateWildcardCache = (key: QueryKey, updater: any) => {
  try {
    const updates = queryClient.setQueriesData(key, updater);
    for (const update of updates) {
      const [key, newData] = update;
      if (newData === undefined) {
        queryClient.invalidateQueries({
          queryKey: key,
          refetchActive: false,
        });
      }
    }
  } catch (e) {
    console.error(e);
  }
};

export const setTest = (newTest: AnteTest) => {
  const updater = (tests: Array<AnteTest>) => {
    if (tests === undefined) return undefined;
    const newTests = [...tests];
    const index = newTests.findIndex(
      (oldTest) =>
        oldTest.address === newTest.address &&
        oldTest.chainId === newTest.chainId
    );
    if (index > -1) {
      newTests[index] = newTest;
    } else {
      newTests.push(newTest);
    }
    return newTests;
  };

  const replacer = (tests: Array<AnteTest>) => {
    if (tests === undefined) return undefined;
    const index = tests.findIndex(
      (oldTest) =>
        oldTest.address === newTest.address &&
        oldTest.chainId === newTest.chainId
    );
    if (index > -1) {
      const newTests = [...tests];
      newTests[index] = newTest;
      return newTests;
    }

    return undefined;
  };

  const offChainReplacer = (response: AnteTest | AnteTest[]) => {
    return Array.isArray(response) ? replacer(response) : response;
  };

  updateOrInvalidateCache(keys.all(), updater);

  queryClient.setQueryData(
    keys.single(newTest.chainId, newTest.address),
    newTest
  );

  updateOrInvalidateWildcardCache(keys.filtered(), replacer);

  updateOrInvalidateWildcardCache(keysPrefix, offChainReplacer);
};

export const TestsCache = {
  keys,
  setTest,
};
